import { INBOX_TYPES } from 'dashboard/helper/inbox';

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const EVOLUTION_DEVICE_WINDOW_IN_MS = 15 * DAY_IN_MS;

const EVOLUTION_EXPIRY_KEYS = [
  'evolution_whatsapp_disconnects_at',
  'evolution_whatsapp_expires_at',
  'evolution_device_disconnects_at',
  'evolution_device_expires_at',
  'evolution_disconnects_at',
  'evolution_expires_at',
];

const GENERIC_EXPIRY_KEYS = [
  'whatsapp_device_disconnects_at',
  'whatsapp_disconnects_at',
  'device_disconnects_at',
  'disconnects_at',
  'expires_at',
];

const EVOLUTION_CONNECTED_KEYS = [
  'evolution_whatsapp_connected_at',
  'evolution_device_connected_at',
  'evolution_connected_at',
  'evolution_last_reconnected_at',
  'evolution_reconnected_at',
  'evolution_pairing_started_at',
];

const GENERIC_CONNECTED_KEYS = [
  'whatsapp_connected_at',
  'device_connected_at',
  'connected_at',
  'last_reconnected_at',
  'reconnected_at',
  'pairing_started_at',
];

const allExpiryKeys = [...EVOLUTION_EXPIRY_KEYS, ...GENERIC_EXPIRY_KEYS];
const allConnectedKeys = [
  ...EVOLUTION_CONNECTED_KEYS,
  ...GENERIC_CONNECTED_KEYS,
];

const parseDateValue = value => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value > 9999999999 ? value : value * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    if (/^\d+(\.\d+)?$/.test(trimmedValue)) {
      return parseDateValue(Number(trimmedValue));
    }

    const date = new Date(trimmedValue);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const findDateValue = (attributes, keys) => {
  const rawValue = keys.map(key => attributes[key]).find(Boolean);
  return parseDateValue(rawValue);
};

const hasAttributeValue = (attributes, keys) =>
  keys.some(key => attributes[key]);

const hasEvolutionCountdownDate = attributes =>
  hasAttributeValue(attributes, EVOLUTION_EXPIRY_KEYS) ||
  hasAttributeValue(attributes, EVOLUTION_CONNECTED_KEYS);

const textIncludes = (values, searchText) =>
  values.some(value =>
    String(value || '')
      .toLowerCase()
      .includes(searchText)
  );

const inboxTextValues = inbox => {
  const attributes = inbox.additional_attributes || {};

  return [
    inbox.name,
    inbox.webhook_url,
    inbox.inbox_identifier,
    attributes.integration,
    attributes.provider,
    attributes.provider_name,
    attributes.source,
    attributes.platform,
    attributes.connector,
    attributes.channel,
    attributes.channel_type,
    attributes.medium,
    attributes.type,
    attributes.vendor,
    attributes.instance_name,
    attributes.instanceName,
    attributes.remote_jid,
    attributes.remoteJid,
    attributes.phone_number,
    attributes.phoneNumber,
  ];
};

const hasWhatsAppMarker = inbox => {
  const attributes = inbox.additional_attributes || {};

  return (
    hasEvolutionCountdownDate(attributes) ||
    Array.isArray(attributes.message_templates) ||
    textIncludes(inboxTextValues(inbox), 'whatsapp') ||
    textIncludes(inboxTextValues(inbox), '@s.whatsapp.net')
  );
};

const hasEvolutionMarker = inbox => {
  const attributes = inbox.additional_attributes || {};

  return (
    hasEvolutionCountdownDate(attributes) ||
    textIncludes(inboxTextValues(inbox), 'evolution')
  );
};

export const isEvolutionWhatsAppInbox = inbox => {
  if (!inbox || inbox.channel_type !== INBOX_TYPES.API) return false;

  return hasEvolutionMarker(inbox) && hasWhatsAppMarker(inbox);
};

const getExpiryDate = inbox => {
  const attributes = inbox.additional_attributes || {};
  const explicitExpiryDate = findDateValue(attributes, allExpiryKeys);

  if (explicitExpiryDate) return explicitExpiryDate;

  const connectedAt = findDateValue(attributes, allConnectedKeys);
  const countdownStart = connectedAt || parseDateValue(inbox.created_at);

  if (!countdownStart) return null;

  return new Date(countdownStart.getTime() + EVOLUTION_DEVICE_WINDOW_IN_MS);
};

export const getEvolutionWhatsAppDeviceStatus = (inbox, now = Date.now()) => {
  if (!isEvolutionWhatsAppInbox(inbox)) return null;

  const expiresAt = getExpiryDate(inbox);
  if (!expiresAt) return null;

  const remainingMs = expiresAt.getTime() - now;

  return {
    expiresAt,
    remainingMs,
    isExpired: remainingMs <= 0,
    isUrgent: remainingMs <= DAY_IN_MS,
  };
};
